// src/components/calendar/MonthCalendar.tsx
import React, { useState, useEffect, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { bookingsAPI, BookingListItem } from '../../services/api';
import { colors } from '../../theme/theme';
import { getCustomIcon } from '../../utils/customIcons';
import DayDetailModal from './DayDetailModal';

const WEEKDAYS_LONG = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAYS_SHORT = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

interface BookingEntry {
  id: number;
  label: string;
}

interface DayEntry {
  checkIns: BookingEntry[];
  checkOuts: BookingEntry[];
}

type DayMap = Record<string, DayEntry>;

interface CalendarDay {
  date: Dayjs;
  isCurrentMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  blacknessOverlay: number;
  textOpacity: number;
}

interface ModalData {
  date: string;
  checkIns: BookingEntry[];
  checkOuts: BookingEntry[];
}

function buildCalendarDays(month: Dayjs): CalendarDay[] {
  const today = dayjs();
  const firstOfMonth = month.startOf('month');
  const startDow = (firstOfMonth.day() + 6) % 7;
  const gridStart = firstOfMonth.subtract(startDow, 'day');

  const days: CalendarDay[] = [];
  for (let i = 0; i < 42; i++) {
    const date = gridStart.add(i, 'day');
    const dow = date.day();
    days.push({
      date,
      isCurrentMonth: date.month() === month.month(),
      isToday: date.isSame(today, 'day'),
      isWeekend: dow === 0 || dow === 6,
      blacknessOverlay: 0,
      textOpacity: 1,
    });
  }

  const last7 = days.slice(35);
  const trimmed = last7.every((d) => !d.isCurrentMonth)
    ? days.slice(0, 35)
    : days;

  const firstCurrentIdx = trimmed.findIndex((d) => d.isCurrentMonth);
  const lastCurrentIdx =
    trimmed.length -
    1 -
    [...trimmed].reverse().findIndex((d) => d.isCurrentMonth);

  // Top group: boundary = darkest (0.9), far edge = lightest
  for (let i = 0; i < firstCurrentIdx; i++) {
    const step = firstCurrentIdx - 1 - i;
    trimmed[i].blacknessOverlay = Math.max(0.1, 0.9 - step * 0.05);
    trimmed[i].textOpacity = Math.max(0.1, 0.5 - step * 0.1);
  }

  // Bottom group: boundary = darkest (0.9), far edge = lightest
  for (let i = lastCurrentIdx + 1; i < trimmed.length; i++) {
    const step = trimmed.length - 1 - i;
    trimmed[i].blacknessOverlay = Math.max(0.1, 0.9 - step * 0.05);
    trimmed[i].textOpacity = Math.max(0.1, 0.5 - step * 0.1);
  }

  return trimmed;
}

function buildDayMap(bookings: BookingListItem[]): DayMap {
  const map: DayMap = {};
  const ensure = (dateStr: string) => {
    if (!map[dateStr]) map[dateStr] = { checkIns: [], checkOuts: [] };
  };
  for (const b of bookings) {
    const label = `${b.clientSurname}, ${b.clientName}`;
    const inDate = dayjs(b.checkInDate).format('YYYY-MM-DD');
    ensure(inDate);
    map[inDate].checkIns.push({ id: b.id, label });
    const outDate = dayjs(b.checkOutDate).format('YYYY-MM-DD');
    ensure(outDate);
    map[outDate].checkOuts.push({ id: b.id, label });
  }
  return map;
}

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const CalendarRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
});

const NavBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 16px',
  backgroundColor: colors.primary.main,
  color: colors.text.light,
  flexShrink: 0,
  gap: '8px',
  [theme.breakpoints.down('sm')]: { padding: '6px 8px' },
}));

const NavCenter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const MonthSelect = styled(Select)(({ theme }) => ({
  color: colors.text.light,
  fontWeight: 700,
  fontSize: '2rem',
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiSelect-icon': { color: colors.text.light },
  '& .MuiSelect-select': { padding: '4px 28px 4px 8px' },
  [theme.breakpoints.down('sm')]: { fontSize: '2rem' },
}));

const YearInput = styled(TextField)(({ theme }) => ({
  width: 'auto',
  '& .MuiInputBase-root': {
    color: colors.text.light,
    fontWeight: 700,
    fontSize: '2rem',
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: '6px',
  },
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& input': {
    padding: '4px 8px',
    textAlign: 'center',
    MozAppearance: 'textfield',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
    },
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiInputBase-root': { fontSize: '2rem' },
  },
}));

const NavIconButton = styled(IconButton)({
  color: colors.text.light,
  scale: 2,
  '&:hover': { backgroundColor: 'transparent', scale: '2' },
});

const WeekdayRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  flexShrink: 0,
  backgroundColor:
    theme.palette.mode === 'dark' ? colors.tertiary.dark : colors.tertiary.main,
}));

const WeekdayCell = styled(Box)(({ theme }) => ({
  padding: '6px 0',
  textAlign: 'center',
  color: colors.text.light,
  fontWeight: 700,
  fontSize: '2rem',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  borderRight: '2px solid rgba(255,255,255,0.1)',
  '&:last-child': { borderRight: 'none' },
  [theme.breakpoints.down('sm')]: { fontSize: '2rem', padding: '4px 0' },
}));

const DayGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gridAutoRows: '1fr',
  height: '100%',
  borderTop: `1px solid ${theme.palette.divider}`,
}));

interface DayCellProps {
  isToday: boolean;
  isWeekend: boolean;
}

const DayCell = styled(Box, {
  shouldForwardProp: (p) => p !== 'isToday' && p !== 'isWeekend',
})<DayCellProps>(({ theme, isToday, isWeekend }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  boxShadow: colors.shadows.powerInsetBold,
  borderRight: `1.5px solid ${theme.palette.primary.dark}`,
  borderBottom: `2px solid ${theme.palette.primary.dark}`,
  overflow: 'hidden',
  cursor: 'pointer',
  position: 'relative',

  backgroundColor: isToday
    ? theme.palette.mode === 'dark'
      ? `${colors.primary.dark}55`
      : `${colors.primary.light}55`
    : isWeekend
      ? theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.03)'
        : 'rgba(0,0,0,0.02)'
      : 'transparent',

  '&:nth-of-type(7n)': { borderRight: 'none' },
  '&:nth-of-type(1n)': {
    backgroundColor: alpha(theme.palette.tertiary.light, 0.5),
  },
  '&:nth-of-type(2n)': {
    backgroundColor: alpha(theme.palette.primary.light, 0.05),
  },

  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : 'rgba(44,90,160,0.06)',
  },
}));

const DateNumber = styled(Typography, {
  shouldForwardProp: (p) =>
    p !== 'isToday' && p !== 'blacknessOverlay' && p !== 'textOpacity',
})<{ isToday: boolean; blacknessOverlay: number; textOpacity: number }>(
  ({ theme, isToday, blacknessOverlay, textOpacity }) => ({
    fontSize: '2rem',
    lineHeight: 1,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.2rem',
    backgroundColor:
      blacknessOverlay > 0
        ? `rgba(0,0,0,${blacknessOverlay})`
        : isToday
          ? colors.secondary.main
          : theme.palette.mode === 'dark'
            ? `${colors.primary.light}55`
            : `${colors.primary.dark}95`,
    color: `rgba(240,248,255,${textOpacity})`,
  })
);

// EntrySummary takes all remaining height, no gap between children
const EntrySummary = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

// Base for both SummaryLine variants
const SummaryLineBase = styled(Box, {
  shouldForwardProp: (p) => p !== 'isToday' && p !== 'blacknessOverlay',
})<{ isToday: boolean; blacknessOverlay: number }>(
  ({ theme, isToday, blacknessOverlay }) => ({
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '4px',
    paddingLeft: '4px',
    backgroundColor:
      blacknessOverlay > 0
        ? `rgba(0,0,0,${blacknessOverlay * 0.6})`
        : isToday
          ? theme.palette.mode === 'dark'
            ? `${colors.secondary.main}55`
            : `${colors.secondary.main}94`
          : 'transparent',

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      justifyContent: 'center',
      paddingLeft: 0,
      gap: 0,
    },
  })
);

// Ghost arrow for mobile - absolutely positioned behind count
const MobileArrowBack = styled(Box, {
  shouldForwardProp: (p) => p !== 'align',
})<{ align: 'left' | 'right' }>(({ theme, align }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    // display: 'flex',
    display: 'none',
    position: 'absolute',
    inset: 0,
    alignItems: 'center',
    justifyContent: align === 'left' ? 'flex-start' : 'flex-end',
    opacity: 0.4,
    pointerEvents: 'none',
  },
}));

// Inline arrow for desktop - shown left of count
const DesktopArrowInline = styled(Box)(({ theme }) => ({
  display: 'flex',
  // display: 'none',
  alignItems: 'center',
  opacity: 0,
  maxWidth: '5px',
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: { display: 'none' },
}));

const CountText = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 800,
  lineHeight: 1,
  position: 'relative',
  zIndex: 1,
  opacity: 0.5,
  [theme.breakpoints.down('sm')]: {
    textAlign: 'center',
  },
}));

const EntryLabel = styled(Typography)(({ theme }) => ({
  fontSize: '1.6rem',
  fontWeight: 600,
  lineHeight: 1,
  position: 'relative',
  zIndex: 1,
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
    opacity: 0.5,
  },
}));

const LoadingOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(128,128,128,0.15)',
  zIndex: 1,
});

// ============================================================================
// COMPONENT
// ============================================================================

const MonthCalendar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [currentMonth, setCurrentMonth] = useState<Dayjs>(
    dayjs().startOf('month')
  );
  const [yearInput, setYearInput] = useState<string>(String(dayjs().year()));
  const [dayMap, setDayMap] = useState<DayMap>({});
  const [loading, setLoading] = useState(false);
  const [modalData, setModalData] = useState<ModalData | null>(null);

  const days = buildCalendarDays(currentMonth);
  const weekdays = isMobile ? WEEKDAYS_SHORT : WEEKDAYS_LONG;

  const fetchMonth = useCallback(async (month: Dayjs) => {
    const gridDays = buildCalendarDays(month);
    setLoading(true);
    try {
      const startDate = gridDays[0].date.format('YYYY-MM-DD');
      const endDate = gridDays[gridDays.length - 1].date.format('YYYY-MM-DD');
      const bookings = await bookingsAPI.getAll({ startDate, endDate });
      setDayMap(buildDayMap(bookings));
    } catch {
      setDayMap({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMonth(currentMonth);
  }, [currentMonth, fetchMonth]);

  const goToPrev = () => {
    const next = currentMonth.subtract(1, 'month');
    setCurrentMonth(next);
    setYearInput(String(next.year()));
  };

  const goToNext = () => {
    const next = currentMonth.add(1, 'month');
    setCurrentMonth(next);
    setYearInput(String(next.year()));
  };

  const handleMonthChange = (value: number) => {
    const next = currentMonth.month(value);
    setCurrentMonth(next);
    setYearInput(String(next.year()));
  };

  const handleYearBlur = () => {
    const parsed = parseInt(yearInput, 10);
    if (!isNaN(parsed) && parsed >= 1900 && parsed <= 2200) {
      setCurrentMonth(currentMonth.year(parsed));
    } else {
      setYearInput(String(currentMonth.year()));
    }
  };

  const handleYearKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleYearBlur();
  };

  const handleDayCellClick = (
    dateStr: string,
    checkIns: BookingEntry[],
    checkOuts: BookingEntry[]
  ) => {
    if (checkIns.length === 0 && checkOuts.length === 0) return;
    setModalData({ date: dateStr, checkIns, checkOuts });
  };

  const handleModalNavigate = (direction: 'prev' | 'next') => {
    if (!modalData) return;
    const newDate = dayjs(modalData.date).add(
      direction === 'next' ? 1 : -1,
      'day'
    );
    const newDateStr = newDate.format('YYYY-MM-DD');
    const { checkIns = [], checkOuts = [] } = dayMap[newDateStr] ?? {};
    setModalData({ date: newDateStr, checkIns, checkOuts });
  };

  // bigarrow points left (check-out). Flipped scaleX(-1) = right (check-in).
  const arrowCheckIn = getCustomIcon(
    'bigarrow',
    isMobile ? '2.7rem' : '1.5rem',
    colors.primary.main
  );
  const arrowCheckOut = getCustomIcon(
    'bigarrow',
    isMobile ? '2.7rem' : '1.5rem',
    colors.tertiary.dark
  );

  return (
    <CalendarRoot>
      <NavBar>
        <NavIconButton
          onClick={goToPrev}
          size="large"
          aria-label="Previous month"
        >
          <ChevronLeftIcon />
        </NavIconButton>

        <NavCenter>
          <MonthSelect
            value={currentMonth.month()}
            onChange={(e) => handleMonthChange(Number(e.target.value))}
            variant="outlined"
            MenuProps={{
              disableScrollLock: true,
              TransitionProps: { onEntering: () => undefined },
              PaperProps: { sx: { borderRadius: '2px', border: '2px solid' } },
            }}
          >
            {MONTHS.map((m, i) => (
              <MenuItem key={m} value={i}>
                {m}
              </MenuItem>
            ))}
          </MonthSelect>

          <YearInput
            type="number"
            value={yearInput}
            onChange={(e) => setYearInput(e.target.value)}
            onBlur={handleYearBlur}
            onKeyDown={handleYearKeyDown}
            variant="outlined"
            inputProps={{ min: 1900, max: 2200 }}
          />
        </NavCenter>

        <NavIconButton onClick={goToNext} size="large" aria-label="Next month">
          <ChevronRightIcon />
        </NavIconButton>
      </NavBar>

      <WeekdayRow>
        {weekdays.map((d, i) => (
          <WeekdayCell key={i}>{d}</WeekdayCell>
        ))}
      </WeekdayRow>

      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <LoadingOverlay>
            <CircularProgress size={32} />
          </LoadingOverlay>
        )}

        <DayGrid>
          {days.map((day) => {
            const dateStr = day.date.format('YYYY-MM-DD');
            const { checkIns = [], checkOuts = [] } = dayMap[dateStr] ?? {};

            return (
              <DayCell
                key={dateStr}
                isToday={day.isToday}
                isWeekend={day.isWeekend}
                onClick={() => handleDayCellClick(dateStr, checkIns, checkOuts)}
              >
                <DateNumber
                  isToday={day.isToday}
                  blacknessOverlay={day.blacknessOverlay}
                  textOpacity={day.textOpacity}
                >
                  {day.date.date()}
                </DateNumber>

                <EntrySummary>
                  <SummaryLineBase
                    isToday={day.isToday}
                    blacknessOverlay={day.blacknessOverlay}
                  >
                    {checkIns.length > 0 && (
                      <>
                        <MobileArrowBack
                          align="right"
                          sx={{ transform: 'scaleX(-1)' }}
                        >
                          {arrowCheckIn}
                        </MobileArrowBack>
                        <DesktopArrowInline sx={{ transform: 'scaleX(-1)' }}>
                          {arrowCheckIn}
                        </DesktopArrowInline>
                        <CountText
                          sx={{
                            color: day.isToday
                              ? colors.text.light
                              : day.isCurrentMonth
                                ? colors.primary.main
                                : alpha(colors.primary.dark, day.textOpacity),
                          }}
                        >
                          {checkIns.length}
                        </CountText>
                        <EntryLabel
                          sx={{
                            color: day.isToday
                              ? colors.text.light
                              : day.isCurrentMonth
                                ? colors.primary.main
                                : alpha(colors.primary.dark, day.textOpacity),
                          }}
                        >
                          In
                        </EntryLabel>
                      </>
                    )}
                  </SummaryLineBase>

                  <SummaryLineBase
                    isToday={day.isToday}
                    blacknessOverlay={day.blacknessOverlay}
                  >
                    {checkOuts.length > 0 && (
                      <>
                        <MobileArrowBack align="right">
                          {arrowCheckOut}
                        </MobileArrowBack>
                        <DesktopArrowInline>{arrowCheckOut}</DesktopArrowInline>
                        <CountText
                          sx={{
                            color: day.isToday
                              ? colors.text.light
                              : day.isCurrentMonth
                                ? colors.tertiary.dark
                                : alpha(colors.primary.dark, day.textOpacity),
                          }}
                        >
                          {checkOuts.length}
                        </CountText>
                        <EntryLabel
                          sx={{
                            color: day.isToday
                              ? colors.text.light
                              : day.isCurrentMonth
                                ? colors.tertiary.dark
                                : alpha(colors.primary.dark, day.textOpacity),
                          }}
                        >
                          Out
                        </EntryLabel>
                      </>
                    )}
                  </SummaryLineBase>
                </EntrySummary>
              </DayCell>
            );
          })}
        </DayGrid>
      </Box>

      {modalData && (
        <DayDetailModal
          open={modalData !== null}
          date={modalData.date}
          checkIns={modalData.checkIns}
          checkOuts={modalData.checkOuts}
          onClose={() => setModalData(null)}
          onPrevDay={() => handleModalNavigate('prev')}
          onNextDay={() => handleModalNavigate('next')}
        />
      )}
    </CalendarRoot>
  );
};

export default MonthCalendar;
